import React, { useState, useEffect } from 'react';
import { apiClient } from '@/api/client';
import { AuditLogViewer } from '@/components/AuditLogViewer';
import { AlertRulesManager } from '@/components/AlertRulesManager';
import { ProductionForecast } from '@/components/ProductionForecast';
import { ComplianceChecklist } from '@/components/ComplianceChecklist';

type TabType = 'audit-log' | 'alert-rules' | 'production-forecast' | 'compliance';

interface AdminPortalState {
  activeTab: TabType;
  auditLog: any[];
  alertRules: any[];
  forecasts: any[];
  complianceTasks: any[];
  products: any[];
  isLoading: boolean;
  error?: string;
}

export function AdminPortal() {
  const [state, setState] = useState<AdminPortalState>({
    activeTab: 'audit-log',
    auditLog: [],
    alertRules: [],
    forecasts: [],
    complianceTasks: [],
    products: [],
    isLoading: true,
  });

  const branchId = localStorage.getItem('selected_branch_id') || '';

  useEffect(() => {
    loadData();
  }, [branchId]);

  const loadData = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const [auditLog, alertRules, forecasts, inventory, complianceTasks] = await Promise.all([
        apiClient.getAuditLog(branchId, 100, 0).then((r) => r.data.entries || []),
        apiClient.getAlertRules(branchId).then((r) => r.data || []),
        apiClient.getDailyForecast(branchId).then((r) => r.data || []),
        apiClient.getInventory(branchId).then((r) => r.data || []),
        apiClient.getComplianceTasks(branchId).then((r) => r.data || []),
      ]).catch((error) => {
        console.error('Error loading admin data:', error);
        return [[], [], [], [], []];
      });

      const products = inventory.map((item: any) => ({
        id: item.productId,
        name: item.productName,
      }));

      setState((prev) => ({
        ...prev,
        auditLog,
        alertRules,
        forecasts,
        complianceTasks,
        products,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to load admin portal data:', error);
      setState((prev) => ({ ...prev, isLoading: false, error: 'Failed to load data' }));
    }
  };

  const handleCreateRule = async (ruleData: any) => {
    try {
      await apiClient.createAlertRule(branchId, ruleData);
      loadData();
    } catch (error) {
      console.error('Failed to create alert rule:', error);
    }
  };

  const handleUpdateRule = async (ruleId: string, updates: any) => {
    try {
      await apiClient.updateAlertRule(ruleId, updates);
      loadData();
    } catch (error) {
      console.error('Failed to update alert rule:', error);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await apiClient.deleteAlertRule(ruleId);
      loadData();
    } catch (error) {
      console.error('Failed to delete alert rule:', error);
    }
  };

  const handleSearchAudit = async (query: string) => {
    if (!query) {
      loadData();
      return;
    }

    try {
      const result = await apiClient.searchAuditLog(branchId, query);
      setState((prev) => ({ ...prev, auditLog: result.data }));
    } catch (error) {
      console.error('Failed to search audit log:', error);
    }
  };

  const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'audit-log', label: 'Audit Log' },
    { id: 'alert-rules', label: 'Alert Rules' },
    { id: 'production-forecast', label: 'Production Forecast' },
    { id: 'compliance', label: 'Compliance' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h1>
        <p className="text-gray-600 mb-6">Manage alerts, audit logs, forecasts, and compliance</p>

        {state.error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{state.error}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setState((prev) => ({ ...prev, activeTab: tab.id }))}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  state.activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {state.isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : (
          <>
            {state.activeTab === 'audit-log' && (
              <AuditLogViewer
                entries={state.auditLog}
                onSearch={handleSearchAudit}
                isLoading={state.isLoading}
              />
            )}

            {state.activeTab === 'alert-rules' && (
              <AlertRulesManager
                rules={state.alertRules}
                products={state.products}
                onCreateRule={handleCreateRule}
                onUpdateRule={handleUpdateRule}
                onDeleteRule={handleDeleteRule}
              />
            )}

            {state.activeTab === 'production-forecast' && (
              <ProductionForecast forecasts={state.forecasts} />
            )}

            {state.activeTab === 'compliance' && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Compliance Task Management</h2>
                <ComplianceChecklist
                  tasks={state.complianceTasks}
                  onTaskComplete={async (taskId, notes) => {
                    const staffId = localStorage.getItem('staff_id') || '';
                    await apiClient.completeComplianceTask(taskId, staffId, notes);
                    loadData();
                  }}
                  onTaskUpdate={async (taskId, updates) => {
                    await apiClient.updateComplianceTask(taskId, updates);
                    loadData();
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
